/**
 * Dashboard page
 * Main dashboard with stats, recent jobs, and system health
 */

import { Row, Col, Typography, Space, Button } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { StatsCards } from '@/components/dashboard/StatsCards';
import { RecentJobsTable } from '@/components/dashboard/RecentJobsTable';
import { SystemHealthCard } from '@/components/dashboard/SystemHealthCard';
import { ActivityChart } from '@/components/dashboard/ActivityChart';
import { useDashboardStats } from '@/hooks/useStats';
import { useJobs } from '@/hooks/useJobs';

const { Title } = Typography;

export function DashboardPage() {
  const navigate = useNavigate();
  const {
    stats,
    activityData,
    systemStats,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useDashboardStats();

  const {
    data: jobsData,
    isLoading: jobsLoading,
    refetch: refetchJobs,
  } = useJobs({ limit: 5, sortBy: 'createdAt', sortOrder: 'desc' });

  const handleRefresh = () => {
    refetchStats();
    refetchJobs();
  };

  return (
    <div>
      <PageHeader
        title="Dashboard"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={statsLoading || jobsLoading}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/jobs/new')}
            >
              New Job
            </Button>
          </Space>
        }
      />

      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        {/* Stats cards */}
        <StatsCards stats={stats} loading={statsLoading} />

        {/* Charts and system health */}
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <ActivityChart data={activityData} loading={statsLoading} />
          </Col>
          <Col xs={24} lg={8}>
            <SystemHealthCard stats={systemStats} loading={statsLoading} />
          </Col>
        </Row>

        {/* Recent jobs */}
        <RecentJobsTable
          jobs={jobsData?.jobs || []}
          loading={jobsLoading}
        />
      </Space>
    </div>
  );
}

export default DashboardPage;
